import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_ORIGIN } from '../../services/apiBase';

const apiBase = () => `${API_ORIGIN}/api/v1`;
const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('access_token')}` });

function KPICard({ label, value, sub, color = '#7b1e3c', target, unit = '' }) {
  const ok = target ? (parseFloat(value) >= target) : true;
  return (
    <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12,
      padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#5e4a53', textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: ok ? color : '#dc2626', lineHeight: 1 }}>
        {value}{unit}
      </div>
      {target && (
        <div style={{ fontSize: 11, color: '#8b7681', marginTop: 4 }}>
          Target: {target}{unit} {ok ? '✓' : '⚠ Below target'}
        </div>
      )}
      {sub && <div style={{ fontSize: 12, color: '#8b7681', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function RevenueCycle() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const canExport = roles.some(r => ['ADMIN', 'BILLING'].includes(r));

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  async function loadSummary() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const r = await fetch(`${apiBase()}/billing/edi/summary?${params}`, { headers: headers() });
      const d = await r.json();
      setSummary(d.data || d);
    } catch { } finally { setLoading(false); }
  }

  useEffect(() => { loadSummary(); }, []);

  async function exportEDI() {
    setExporting(true); setExportMsg('');
    try {
      const params = new URLSearchParams({ format: 'edi' });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const r = await fetch(`${apiBase()}/billing/edi/837?${params}`, { headers: headers() });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `837p_${dateFrom || 'all'}_${dateTo || 'all'}.txt`;
      a.click(); URL.revokeObjectURL(url);
      setExportMsg('EDI file downloaded ✓');
    } catch (err) {
      setExportMsg(`Export failed: ${err.message}`);
    } finally { setExporting(false); }
  }

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2b1b22', margin: 0 }}>
          Revenue Cycle Management
        </h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 12 }} />
          <span style={{ color: '#8b7681', fontSize: 12 }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 12 }} />
          <button onClick={loadSummary} style={{ padding: '7px 14px', background: '#f3f4f6',
            border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
            Refresh
          </button>
          {canExport && (
            <button onClick={exportEDI} disabled={exporting} style={{
              padding: '7px 14px', background: exporting ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
              color: '#fff', border: 'none', borderRadius: 8, fontSize: 12,
              fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif',
            }}>{exporting ? 'Generating…' : '⬇ Export 837P'}</button>
          )}
        </div>
      </div>

      {exportMsg && (
        <div style={{ background: exportMsg.includes('failed') ? '#fdecea' : '#dcfce7',
          color: exportMsg.includes('failed') ? '#b71c1c' : '#166534',
          padding: '8px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {exportMsg}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#8b7681' }}>Loading revenue data…</div>
      ) : !summary ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8b7681' }}>No data available.</div>
      ) : (
        <>
          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 16, marginBottom: 24 }}>
            <KPICard label="Total Claims" value={summary.total_claims} />
            <KPICard label="Total Billed"
              value={`$${(summary.billed || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`} />
            <KPICard label="Collected"
              value={`$${(summary.collected || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`}
              color="#166534" />
            <KPICard label="Collection Rate" value={summary.collection_rate_pct} unit="%"
              target={90} color="#166534" />
            <KPICard label="Denial Rate" value={summary.denial_rate_pct} unit="%"
              color={summary.denial_rate_pct < 5 ? '#166534' : '#dc2626'} />
            <KPICard label="Clean Claim Rate" value={summary.clean_claim_rate_pct} unit="%"
              target={95} color="#166534" />
            <KPICard label="Avg Days to Payment"
              value={summary.avg_days_to_payment} unit=" days"
              color={summary.avg_days_to_payment < 30 ? '#166534' : '#dc2626'} />
          </div>

          {/* Claim status breakdown */}
          <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12,
            padding: 20, marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 16px' }}>Claim Status Breakdown</h2>
            <div style={{ display: 'flex', gap: 16 }}>
              {[
                { label: 'Paid', count: summary.paid, color: '#166534', bg: '#dcfce7' },
                { label: 'Pending', count: summary.pending, color: '#92400e', bg: '#fef9c3' },
                { label: 'Denied', count: summary.denied, color: '#991b1b', bg: '#fee2e2' },
              ].map(({ label, count, color, bg }) => (
                <div key={label} style={{ flex: 1, background: bg, borderRadius: 10,
                  padding: '14px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{count}</div>
                  <div style={{ fontSize: 12, color, fontWeight: 600, marginTop: 4 }}>{label}</div>
                  <div style={{ fontSize: 11, color, opacity: 0.7, marginTop: 2 }}>
                    {summary.total_claims ? Math.round((count / summary.total_claims) * 100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CMS targets reference */}
          <div style={{ background: '#f9f5f6', borderRadius: 12, padding: '14px 20px',
            border: '1px solid #ece3e7', fontSize: 12, color: '#5e4a53' }}>
            <strong>CMS Benchmark Targets:</strong> Clean Claim Rate &gt;95% | Denial Rate &lt;5% | Days in AR &lt;30 | Collection Rate &gt;95%
          </div>
        </>
      )}
    </div>
  );
}
